import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/outline';
import { motion } from 'framer-motion';
import React from 'react';
import { useRecoilValue } from 'recoil';

import { useUIControl } from '@/components/PdfReader/useUIControl';

import { aiModalOpenAtom } from '@/atoms/pdfStates/aiModalOpenAtom';

type PageNavigatorProps = {
  pageNumber: number;
  numPages: number;
  setPageNumber: (page: number) => void;
  getNextPage: () => Promise<number | null>;
  getPreviousPage: () => Promise<number | null>;
  leftButtonRef: React.RefObject<HTMLButtonElement>;
  rightButtonRef: React.RefObject<HTMLButtonElement>;
  wrapperRef: React.RefObject<HTMLDivElement>;
};

const PageNavigator: React.FC<PageNavigatorProps> = ({
  pageNumber,
  numPages,
  setPageNumber,
  getNextPage,
  getPreviousPage,
  leftButtonRef,
  rightButtonRef,
  wrapperRef,
}) => {
  const openDrawer = useRecoilValue<boolean>(aiModalOpenAtom);
  const { clearOverlays } = useUIControl();

  return (
    <motion.div
      ref={wrapperRef}
      className='fixed z-50 bg-wh bottom-0 md:bottom-[3%] w-full md:w-auto 
             font-semibold text-white inline-flex items-center
             left-1/2 -translate-x-1/2'
      initial={{
        left: openDrawer ? 'calc(50% + 12vw)' : '50%',
      }}
      animate={{
        left: openDrawer ? 'calc(50% + 12vw)' : '50%',
      }}
      transition={{
        type: 'spring',
        stiffness: 100,
        damping: 18,
        mass: 0.6,
      }}
    >
      <div className='w-full flex justify-center relative bg-black/90 md:bg-transparent'>
        <div className='flex w-fit justify-center'>
          <div
            className='flex items-center md:bg-black/90 md:rounded-xl w-full justify-between shadow-xl py-1 md:py-0 md:border md:border-white/10'
            onClick={(e) => e.stopPropagation()}
          >
            {pageNumber > 1 && (
              <button
                ref={leftButtonRef}
                onClick={async () => {
                  const prev = await getPreviousPage();
                  if (prev) setPageNumber(prev);
                }}
                className='flex justify-center items-center w-10 h-10 md:hover:bg-white/20 rounded-l-xl transition-all mr-1'
                aria-label='Previous Page'
              >
                <ChevronLeftIcon className='md:w-5 md:h-5 w-4 h-4 text-white' />
              </button>
            )}

            <span
              onClick={(e) => {
                e.stopPropagation();
                clearOverlays();
              }}
              className='px-2 py-0.5 text-white text-sm inline-flex gap-x-2.5 font-medium cursor-pointer select-none'
            >
              <p className='text-white'>{pageNumber}</p> /{' '}
              <p className='text-grayZl-500'>{numPages}</p>
            </span>

            {pageNumber < numPages && (
              <button
                ref={rightButtonRef}
                onClick={async () => {
                  const next = await getNextPage();
                  if (next) setPageNumber(next);
                }}
                className='flex justify-center items-center w-10 h-10 md:hover:bg-white/20 rounded-r-xl transition-all'
                aria-label='Next Page'
              >
                <ChevronRightIcon className='md:w-5 md:h-5 w-4 h-4 text-white' />
              </button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default PageNavigator;
